import { forwardRef, useMemo } from "react";
import { type Column } from "@racoon/ui";
import { useT, Namespace, TranslationKey } from "@racoon/i18n";
import { RequestReportRow, useRequestReportRows } from "../model/use-request-report-rows";
import { useReportHyperlinks } from "../model/use-report-hyperlinks";
import {
  amountCell,
  byNumber,
  compareIds,
  entityNumber,
  numberColumn,
  reportDate,
  reportDateTime,
  sumOf,
  textCell,
} from "../model/job-report";
import { ReportTable, ReportTableHandle, ReportTableProps } from "./report-table";

/** A report row, or the totals line that closes the exports. */
type RequestExportRow = RequestReportRow & { isTotal?: boolean };

/** «Заявки» (AIS 15.1.1): every request for the collection dates in the period. */
export const RequestsReportTable = forwardRef<ReportTableHandle, ReportTableProps>((props, ref) => {
  const t = useT();
  const { rows, isLoading, error } = useRequestReportRows(props.startDate, props.endDate);
  const { locationCell, customerCell, entityCards } = useReportHyperlinks();

  const columns: Column<RequestExportRow>[] = useMemo(
    () => [
      numberColumn(
        t(Namespace.Analytics, "columns.number"),
        t(Namespace.Analytics, "collection.totals.label"),
      ),
      {
        id: "id",
        header: t(Namespace.Analytics, "columns.id"),
        accessor: (row) => (row.isTotal ? "" : entityNumber("job", row.jobId)),
        width: 100,
        sortable: true,
        sortFn: (a, b) => compareIds(a.jobId, b.jobId),
      },
      {
        id: "exportDate",
        header: t(Namespace.Analytics, "columns.exportDate"),
        accessor: (row) => (row.isTotal ? "" : reportDate(row.twStartAt)),
        width: 120,
        sortable: true,
        sortFn: byNumber((row) => row.twStartAt?.getTime()),
      },
      {
        id: "client",
        header: t(Namespace.Analytics, "table.customer"),
        key: "serviceReceiverName",
        width: 200,
        sortable: true,
        cell: (info) =>
          customerCell(info.row.original.serviceReceiverId, info.getValue() as string),
      },
      {
        id: "bin",
        header: t(Namespace.Analytics, "columns.bin"),
        accessor: (row) => textCell(row.serviceReceiverBin, row.isTotal),
        width: 130,
        sortable: true,
      },
      {
        id: "site",
        header: t(Namespace.Analytics, "columns.site"),
        key: "pickupLocationName",
        width: 200,
        sortable: true,
        cell: (info) => locationCell(info.row.original.pickupLocationId, info.getValue() as string),
      },
      {
        id: "containerType",
        header: t(Namespace.Analytics, "columns.containerType"),
        accessor: (row) => textCell(row.containerTypeName, row.isTotal),
        width: 150,
        sortable: true,
      },
      {
        id: "totalCount",
        header: t(Namespace.Analytics, "requests.totalCount"),
        accessor: (row) => amountCell(row.numberOfContainers, 0, row.isTotal),
        width: 110,
        sortable: true,
        sortFn: byNumber((row) => row.numberOfContainers),
      },
      {
        id: "totalVolume",
        header: t(Namespace.Analytics, "columns.nominalVolume"),
        accessor: (row) => amountCell(row.containerVolume, 2, row.isTotal),
        width: 130,
        sortable: true,
        sortFn: byNumber((row) => row.containerVolume),
      },
      {
        id: "factVolume",
        header: t(Namespace.Analytics, "columns.factVolume"),
        accessor: (row) => amountCell(row.factTotalVolume, 2, row.isTotal),
        width: 130,
        sortable: true,
        sortFn: byNumber((row) => row.factTotalVolume),
      },
      {
        id: "status",
        header: t(Namespace.Analytics, "requests.status"),
        accessor: (row) =>
          row.isTotal ? "" : t(Namespace.Enum, `JOB_STATUS_${row.status}` as TranslationKey),
        width: 130,
        sortable: true,
        sortFn: (a, b) => a.status.localeCompare(b.status),
      },
      {
        id: "createdAt",
        header: t(Namespace.Analytics, "requests.createdAt"),
        accessor: (row) => (row.isTotal ? "" : reportDateTime(row.createdAt)),
        width: 150,
        sortable: true,
        sortFn: byNumber((row) => row.createdAt?.getTime()),
      },
      {
        id: "updatedAt",
        header: t(Namespace.Analytics, "requests.updatedAt"),
        accessor: (row) => (row.isTotal ? "" : reportDateTime(row.updatedAt)),
        width: 170,
        sortable: true,
        sortFn: byNumber((row) => row.updatedAt?.getTime()),
      },
    ],
    [t, locationCell, customerCell],
  );

  const exportRows = useMemo<RequestExportRow[]>(
    () =>
      rows.length
        ? [
            ...rows,
            {
              isTotal: true,
              jobId: 0n,
              twStartAt: null,
              serviceReceiverId: null,
              serviceReceiverName: "",
              serviceReceiverBin: "",
              pickupLocationId: null,
              pickupLocationName: "",
              containerTypeId: "",
              containerTypeName: "",
              numberOfContainers: sumOf(rows.map((r) => r.numberOfContainers)),
              containerVolume: sumOf(rows.map((r) => r.containerVolume)),
              factTotalVolume: sumOf(rows.map((r) => r.factTotalVolume)),
              status: "",
              createdAt: null,
              updatedAt: null,
            },
          ]
        : rows,
    [rows],
  );

  return (
    <ReportTable
      {...props}
      exportRef={ref}
      settingsKey="requests_report"
      reportName={t(Namespace.Analytics, "reports.requests")}
      columns={columns}
      rows={rows}
      exportRows={exportRows}
      rowKey={(row) => String(row.jobId)}
      isLoading={isLoading}
      error={error}
    >
      {entityCards}
    </ReportTable>
  );
});
